import { Box } from '@chakra-ui/react';
import { useAppSelector } from 'modules/Editor/store';
import {
  selectAllPageBackgroundData,
  selectPageNumberCustomRangeAsObject,
  selectPageNumberShowOnPages,
  selectPageNumberStart,
  selectPageOrder,
} from 'modules/Editor/store/infographSelector';
import { memo } from 'react';
import { isLight } from 'utils/color';

import { DEFAULT_STYLE, EDITABLE_PAGE_NUMBER_ID, PAGE_NUMBER_EXPORT_CLASSNAME, left, top } from './PageNumber.config';
import { getPageColor } from './PageNumber.helpers';
import { ColorContrastProps, PageNumberDisplayProps, PageNumberProps } from './PageNumber.types';

export const PageNumber = memo(({ pageId, zIndex, isReadOnly }: PageNumberProps) => {
  const pageOrder = useAppSelector(selectPageOrder);
  const pageNumberStart = useAppSelector(selectPageNumberStart);
  const customPageRange = useAppSelector(selectPageNumberCustomRangeAsObject);
  const showOnPages = useAppSelector(selectPageNumberShowOnPages);

  const pageNumber = pageOrder.indexOf(pageId) + 1;
  const hasCustomPageRange = showOnPages === 'custom';

  // if the page number is less than the start page number
  if (!hasCustomPageRange && pageNumberStart > pageNumber) return null;
  // if the page number is not in the custom page range
  if (hasCustomPageRange && !customPageRange[pageNumber]) return null;

  return <PageNumberDisplay isReadOnly={isReadOnly} pageNumber={pageNumber} zIndex={zIndex} pageId={pageId} />;
});
PageNumber.displayName = 'PageNumber';

const pageColorContrastTestId = 'page-color-contrast';

// Sets the color of the page number depending on
// 1. The background color of the page
// 2. The color of the widget behind the page number (TODO: implement)
const ColorContrast = ({ pageId, children }: ColorContrastProps) => {
  // TODO: probably could update this to a debounced selector
  const pageBackground = useAppSelector(selectAllPageBackgroundData(pageId));
  const color = isLight(getPageColor(pageBackground)) ? '#000000' : '#ffffff';

  return (
    <Box data-testid={pageColorContrastTestId} color={color}>
      {children}
    </Box>
  );
};

const PageNumberDisplay = memo(({ pageNumber, zIndex, pageId, isReadOnly }: PageNumberDisplayProps) => {
  // Only the page number on the editable page has this id
  const id = isReadOnly ? undefined : EDITABLE_PAGE_NUMBER_ID;

  return (
    <ColorContrast pageId={pageId}>
      <Box
        as={'p'}
        fontSize={DEFAULT_STYLE.fontSize + 'px'}
        fontFamily={DEFAULT_STYLE.fontFamily}
        fontWeight={DEFAULT_STYLE.fontWeight}
        fontStyle={DEFAULT_STYLE.fontStyle}
        textDecoration={DEFAULT_STYLE.textDecoration}
        position='absolute'
        top={top}
        left={left}
        zIndex={zIndex}
        id={id}
        data-testid={`page-number-${pageId}`}
        className={PAGE_NUMBER_EXPORT_CLASSNAME}
        userSelect='none'
      >
        {pageNumber}
      </Box>
    </ColorContrast>
  );
});
PageNumberDisplay.displayName = 'PageNumberDisplay';
